"use client";

import { useEffect, useState } from "react";
import { Badge, Button, Card, Spinner } from "@/components/ui";
import { CurrencySelect, useCurrency } from "@/components/currency-provider";
import { WALLET_PACKAGES } from "@/lib/wallet-packages";

interface LedgerEntry {
  id: number;
  amountCents: number;
  reason: "trial" | "purchase" | "debit" | "refund" | "adjustment";
  note?: string;
  createdAt: string;
}

const REASON_LABEL: Record<LedgerEntry["reason"], string> = {
  trial: "Welcome balance",
  purchase: "Purchase",
  debit: "Video render",
  refund: "Refund",
  adjustment: "Adjustment",
};

export default function BillingPage() {
  const { currency, formatUsdCents, exactUsdCents, asOf } = useCurrency();
  const [data, setData] = useState<{
    balanceCents: number;
    entries: LedgerEntry[];
  } | null>(null);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/ledger")
      .then((r) => r.json())
      .then((d) => alive && setData(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  async function startCheckout(packageId: string) {
    setCheckoutId(packageId);
    setCheckoutError(null);
    try {
      const response = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ packageId }),
      });
      const result = (await response.json().catch(() => ({}))) as {
        url?: string;
        error?: string;
      };
      if (!response.ok || !result.url) {
        throw new Error(result.error || "Unable to start checkout");
      }
      window.location.assign(result.url);
    } catch (error) {
      setCheckoutError(
        error instanceof Error ? error.message : "Unable to start checkout",
      );
      setCheckoutId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-8 sm:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <CurrencySelect className="min-w-48" />
      </div>
      <p className="mt-2 text-sm text-muted">
        Local currency values are estimates. Charges and automatic refunds use your USD wallet.
      </p>

      {data === null ? (
        <div className="grid place-items-center py-20 text-muted">
          <Spinner className="size-6" />
        </div>
      ) : (
        <>
          <Card className="mt-8 flex items-center justify-between p-6">
            <div>
              <div className="text-[13px] text-muted">Current balance</div>
              <div className="mt-1 text-3xl font-semibold tracking-tight">
                {formatUsdCents(data.balanceCents)}
              </div>
              {currency !== "USD" && (
                <div className="mt-1 text-[11px] text-muted">
                  {exactUsdCents(data.balanceCents)} wallet
                  {asOf ? ` · FX rate ${asOf}` : ""}
                </div>
              )}
            </div>
          </Card>

          <div className="mt-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Add funds</h2>
                <p className="mt-1 text-[13px] text-muted">
                  Choose an amount and continue to secure Stripe Checkout.
                </p>
              </div>
              <Badge tone="success">Secure checkout</Badge>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {WALLET_PACKAGES.map((walletPackage) => (
                <Card
                  key={walletPackage.id}
                  className={
                    "relative flex flex-col p-5 " +
                    ("popular" in walletPackage && walletPackage.popular
                      ? "border-[var(--accent)]/60 bg-[var(--accent)]/8"
                      : "")
                  }
                >
                  {"popular" in walletPackage && walletPackage.popular && (
                    <Badge
                      tone="accent"
                      className="absolute -top-3 right-3 z-10 shadow-lg"
                    >
                      Popular
                    </Badge>
                  )}
                  <div className="text-2xl font-semibold">
                    {formatUsdCents(walletPackage.amountCents)}
                  </div>
                  {currency !== "USD" && (
                    <div className="mt-1 text-[11px] text-muted">
                      Charged as {walletPackage.label} USD
                    </div>
                  )}
                  <div className="mt-auto pt-5">
                    <Button
                      size="sm"
                      variant={
                        "popular" in walletPackage && walletPackage.popular
                          ? "primary"
                          : "secondary"
                      }
                      className="w-full"
                      disabled={checkoutId !== null}
                      onClick={() => void startCheckout(walletPackage.id)}
                    >
                      {checkoutId === walletPackage.id ? (
                        <>
                          <Spinner /> Opening…
                        </>
                      ) : (
                        "Select"
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
            {checkoutError && (
              <div className="mt-3 rounded-xl border border-[var(--danger)]/35 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
                {checkoutError}
              </div>
            )}
            <p className="mt-3 text-[11px] leading-5 text-muted">
              Funds are stored as USD cash balance for EasyShot services. Local
              currency values are estimates; your bank or Stripe may apply its
              own conversion rate.
            </p>
          </div>

          <h2 className="mt-10 mb-3 text-sm font-medium text-muted">History</h2>
          <Card className="divide-y divide-[var(--border)] p-0">
            {data.entries.length === 0 && (
              <div className="p-6 text-sm text-muted">No activity yet.</div>
            )}
            {data.entries.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-4 px-5 py-3.5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    {REASON_LABEL[e.reason]}
                    {e.reason === "refund" && <Badge tone="success">Refunded</Badge>}
                  </div>
                  <div className="mt-0.5 truncate text-[12px] text-muted">
                    {e.note ?? "—"} ·{" "}
                    {new Date(e.createdAt).toLocaleString("en-US")}
                  </div>
                </div>
                <div
                  className={
                    e.amountCents > 0
                      ? "font-mono text-sm text-[var(--success)]"
                      : "font-mono text-sm"
                  }
                >
                  {e.amountCents > 0 ? "+" : "-"}
                  {formatUsdCents(Math.abs(e.amountCents))}
                </div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
